"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";

/**
 * useReveal — triggers a GSAP animation once the element enters the viewport.
 * Uses IntersectionObserver (not ScrollTrigger) to avoid scroll-lag issues.
 *
 * @param {Function} build  (el) => gsap timeline/tween. Called once when el intersects.
 * @param {{threshold?: number, rootMargin?: string, once?: boolean}} opts
 */
export function useReveal(build, opts = {}) {
  const ref = useRef(null);
  const { threshold = 0.2, rootMargin = "0px 0px -10% 0px", once = true } = opts;

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return;

    let tween;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            tween = build(el);
            if (once) io.disconnect();
          }
        });
      },
      { threshold, rootMargin }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      if (tween && typeof tween.kill === "function") tween.kill();
    };
  }, [build, once, rootMargin, threshold]);

  return ref;
}

/**
 * Split a string into word spans + (optionally) char spans for GSAP staggering.
 */
export function SplitText({ text, className = "", mode = "word", as: Tag = "span" }) {
  if (mode === "char") {
    return (
      <Tag className={`reveal-split ${className}`} aria-label={text}>
        {text.split("").map((ch, i) => (
          <span key={i} className="char" aria-hidden="true">
            {ch === " " ? "\u00A0" : ch}
          </span>
        ))}
      </Tag>
    );
  }

  return (
    <Tag className={`reveal-split ${className}`} aria-label={text}>
      {text.split(" ").map((word, i) => (
        <span key={i} className="word" aria-hidden="true" style={{ marginRight: "0.25em" }}>
          {word}
        </span>
      ))}
    </Tag>
  );
}
